import React from 'react';
import I18n from '../i18n';
import PropTypes from 'prop-types';
import useIdentity from '../../hooks/use-identity';
import NavigationApplication from './application';
import ActiveApplicationContext from '../../contexts/active-application-context';
import LaunchpadApplication from './launchpad';

function filterApps(apps, usersApps = []) {
  for (let i = apps.length - 1; i >= 0; i--) {
    const app = apps[i];
    if (usersApps.indexOf(app.id) < 0) {
      apps.splice(i, 1);
    }
  }
}

function filterNav(items, can) {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (item.children) {
      filterNav(item.children, can);
    }
    if ((item.children !== undefined && item.children.length === 0) || (item.permission && !can(...item.permission))) {
      items.splice(i, 1);
    }
  }
}

export default function Navigation(props) {
  const {application} = React.useContext(ActiveApplicationContext);
  const {applications, open, onRequestOpen, launchpadConfig} = props;
  const year = new Date().getFullYear();
  const [openApplications, setOpenApplications] = React.useState(createAppOpenStatesHash());
  const identity = useIdentity();

  const clonedApplications = React.useMemo(() => {
    const clonedApplications = JSON.parse(JSON.stringify(applications));
    filterApps(clonedApplications, identity.user.applications);
    filterNav(clonedApplications, identity.can);
    return clonedApplications;
  }, [applications, identity]);

  const onApplicationOpenToggled = toggleAppId => {
    const wasOpen = openApplications[toggleAppId];
    const openStates = createAppOpenStatesHash();
    openStates[toggleAppId] = applications.length > 1 ? !wasOpen : true;
    setOpenApplications(openStates);
    if (!wasOpen && onRequestOpen) {
      onRequestOpen();
    }
  };

  React.useEffect(() => {
    if (!open) {
      setOpenApplications(createAppOpenStatesHash());
    }
  }, [open]);

  React.useEffect(() => {
    const openStates = createAppOpenStatesHash();
    openStates[application.id] = true;
    setOpenApplications(openStates);
  }, [application]);

  function createAppOpenStatesHash() {
    return applications.reduce((prev, app) => {
      prev[app.id] = false;
      return prev;
    }, {});
  }

  return (
    <div className={`navigation-component ${open ? 'nav-open' : ''}`}>
      <div className="navigation-view">
        <div className="application-wrapper">
          <LaunchpadApplication config={launchpadConfig} />
          {clonedApplications.map(app => (
            <NavigationApplication
              app={app}
              open={open && (openApplications[app.id] || applications.length === 1)}
              onOpenToggled={onApplicationOpenToggled}
              key={app.id}
              selected={app.id === application.id}
            />
          ))}
        </div>
        <div className="copyright">
          <I18n t="footer-copyright" opts={{year}} />
          <br />
          <I18n t="footer-copyright-reserved" />
        </div>
      </div>
    </div>
  );
}

Navigation.defaultProps = {
  open: true,
};

Navigation.propTypes = {
  /** The array of applications and their navigation items to display */
  applications: PropTypes.array.isRequired,
  /** Whether the menu is open (true) or closed (false) */
  open: PropTypes.bool,
  /** A callback used to request that this nav panel be openend. */
  onRequestOpen: PropTypes.func,
  /** The launchpad configuration. Should be an object containing a `uiUrl`. */
  launchpadConfig: PropTypes.object,
};
